/**
 * What each report shows and when to reach for it (#639), said once at the top
 * of the page so nobody meets a chart before knowing what the page is for.
 *
 * Keyed by the section id in `config/modules.ts`; `check:ux-reports` fails if
 * a report section has no entry here.
 */
export interface ReportDescription {
  /** One sentence: what the report counts. */
  what: string;
  /** One sentence: when it is the page to open. */
  when: string;
}

export const REPORT_DESCRIPTIONS: Readonly<Record<string, ReportDescription>> =
  {
    sales: {
      what: "Revenue over time, by shipping state and by payment status, for the dates you choose.",
      when: "Use it to see how a week or a month went, and where the money came from.",
    },
    orders: {
      what: "How many orders came in per day, week or month, or what they were worth, optionally split by a field.",
      when: "Use it to compare periods or channels; it takes the same filters as the Orders list.",
    },
    delivery: {
      what: "RTO rate, delivery success, first-attempt delivery and speed, by courier, state and payment method.",
      when: "Use it to compare couriers and states, and to spot a courier that is slipping.",
    },
    products: {
      what: "Best sellers by units and by revenue across every order, and how each SKU sells over a range of dates.",
      when: "Use it before reordering stock: what moves fastest is not always what earns most, and SKU trends show whether it is speeding up or slowing down.",
    },
    inventory: {
      what: "Stock on hand, low-stock variants and units in transit, by warehouse.",
      when: "Use it to see where stock sits and which warehouse is running low.",
    },
    gst: {
      what: "Tax collected on issued invoices, split by CGST, SGST, IGST and cess, and the taxable value by HSN code.",
      when: "Use it to check the tax before filing; drafts and cancelled invoices are left out.",
    },
    returns: {
      what: "Every item a returned parcel brought back damaged or did not bring back, with the reason and the photo.",
      when: "Use it to see what is lost in returns and why, before raising it with a courier.",
    },
  };
